/** Platform notices (client MCP plan §5.2 "Was it you?", §7.1; backend WPS P1-08): ops' word on a
 *  problem on our side, quoted to clients' AI by the triage check `platform_notice` — the
 *  capability, the window and the client wording, for every tenant or the tenants listed. A
 *  client's AI never sees the audience, the tenant list, the internal note or the author.
 *
 *  Every endpoint needs `console_platform_notices_manage` (Ops Admin by default):
 *  - `GET /platform_notices/` — paginated, the latest start first; `GET …/capabilities/` — the
 *    capabilities in the client words the check uses ("a problem with <label>");
 *  - `POST` / `PATCH /{id}/` / `DELETE /{id}/` — each audited (`platform_notice.created` /
 *    `.updated` / `.deleted`); the notice's edits keep their field history (objchange, content
 *    type `demo.platform_notice`), with `X-Change-Reason` as the row's comment. A refusal is a
 *    field-keyed 400 (a wording that is not client words, an end not after the start, a tenant
 *    list that does not fit the audience).
 *
 *  Shapes come from the generated spec types; this module adds the query keys and the words. */
import { apiDelete, apiGet, apiPatch, apiPost, ConsoleApiError, withChangeReason } from './client';
import type { components } from './types';
import type { PaginatedResponse } from './users';
import type { TenantIdsParam } from '@/scope/tenantIds';

type Schemas = components['schemas'];

export type PlatformNotice = Schemas['ConsolePlatformNotice'];
export type PlatformNoticeWrite = Schemas['ConsolePlatformNoticeWrite'];
export type PlatformNoticeCapabilityKey = Schemas['ConsolePlatformNoticeCapabilityKey'];
export type PlatformNoticeCapabilities = Schemas['ConsolePlatformNoticeCapabilities'];
export type PlatformNoticeAudience = PlatformNotice['audience'];

/** The objchange content type of a notice (its Change history). */
export const PLATFORM_NOTICE_CONTENT_TYPE = 'demo.platform_notice';

/** The backend's limits (`demo.lib.triage.notices`, the serializer). */
export const WORDING_MAX = 500;
export const INTERNAL_NOTE_MAX = 2000;
export const MAX_TENANTS = 200;

/** The console audit log's events for a notice. */
export const PLATFORM_NOTICE_AUDIT_EVENT_PREFIX = 'platform_notice.';

export interface PlatformNoticeFilters {
  /** One capability key or a comma-separated list. */
  capability?: string;
  audience?: PlatformNoticeAudience;
  /** true: started and not ended now; false: the rest. */
  active?: boolean;
  /** The notices that apply to these tenants: every tenant's and those listing one of them. */
  tour_operator_id?: TenantIdsParam;
  /** Text in the wording or the internal note. */
  search?: string;
}

export interface PlatformNoticeListParams extends PlatformNoticeFilters {
  ordering?: string;
  page?: number;
  page_size?: number;
}

type QueryValue = string | number | boolean | null | undefined;

function toQuery(params: Record<string, QueryValue>): string {
  const usp = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === '') continue;
    usp.set(key, String(value));
  }
  const qs = usp.toString();
  return qs ? `?${qs}` : '';
}

export function list(
  params: PlatformNoticeListParams = {},
): Promise<PaginatedResponse<PlatformNotice>> {
  return apiGet(`/platform_notices/${toQuery({ ...params })}`);
}

export function retrieve(id: number): Promise<PlatformNotice> {
  return apiGet(`/platform_notices/${id}/`);
}

export function capabilities(): Promise<PlatformNoticeCapabilities> {
  return apiGet('/platform_notices/capabilities/');
}

export function create(body: PlatformNoticeWrite, reason?: string): Promise<PlatformNotice> {
  return apiPost('/platform_notices/', body, withChangeReason(reason));
}

/** A partial update: send only what changed (the audit row lists the fields sent). */
export function update(
  id: number,
  body: PlatformNoticeWrite,
  reason?: string,
): Promise<PlatformNotice> {
  return apiPatch(`/platform_notices/${id}/`, body, withChangeReason(reason));
}

export function remove(id: number): Promise<void> {
  return apiDelete(`/platform_notices/${id}/`);
}

export const platformNoticeKeys = {
  all: ['platformNotices'] as const,
  list: ['platformNotices', 'list'] as const,
  detail: (id: number) => ['platformNotices', 'detail', id] as const,
  capabilities: ['platformNotices', 'capabilities'] as const,
};

/** The fields a refusal may name, in the form's order. */
export const WRITE_FIELDS = [
  'capability',
  'starts_at',
  'ends_at',
  'wording',
  'audience',
  'tenants',
  'internal_note',
] as const;
export type WriteField = (typeof WRITE_FIELDS)[number];

const firstMessage = (v: unknown): string | null => {
  if (typeof v === 'string' && v) return v;
  if (Array.isArray(v)) {
    const s = v.find((x): x is string => typeof x === 'string' && x !== '');
    return s ?? null;
  }
  return null;
};

/** A refused save, read: the field-keyed messages (per form field) and the rest (`detail`,
 *  `non_field_errors`, unknown keys, or the transport error). */
export function writeErrorsOf(e: unknown): {
  fields: Partial<Record<WriteField, string>>;
  general: string | null;
} {
  if (!(e instanceof ConsoleApiError)) {
    return { fields: {}, general: e instanceof Error ? e.message : 'Save failed.' };
  }
  const body = (e.body ?? {}) as Record<string, unknown>;
  const fields: Partial<Record<WriteField, string>> = {};
  const rest: string[] = [];
  for (const [key, value] of Object.entries(body)) {
    const msg = firstMessage(value);
    if (!msg) continue;
    if ((WRITE_FIELDS as readonly string[]).includes(key)) fields[key as WriteField] = msg;
    else rest.push(key === 'detail' || key === 'non_field_errors' ? msg : `${key}: ${msg}`);
  }
  const general = rest.length ? rest.join(' ') : Object.keys(fields).length ? null : e.message;
  return { fields, general: general || null };
}
